import type { Snippet, SnippetValues } from './embed-snippets.ts';

export function backendSnippets(values: SnippetValues): Snippet[] {
  return [environment(values), express(values), nextjs(values), python(values)];
}

function environment(values: SnippetValues): Snippet {
  return {
    id: 'env',
    title: 'Your secret key, in your server environment',
    language: 'shell',
    code: `PLATO_SECRET_KEY=plato_sk_${values.mode}_...   # shown once when your tenant was created; never in the browser`,
  };
}

function express(values: SnippetValues): Snippet {
  return {
    id: 'express',
    title: 'Express',
    language: 'ts',
    code: `import { createPlatoSession } from '@plato/node';

// requireUser: your own sign-in middleware; it answers 401 when nobody is signed in.
app.post('${values.sessionPath}', requireUser, async (req, res) => {
  try {
    // Reads PLATO_SECRET_KEY from the environment.
    const session = await createPlatoSession({${apiOriginLine(values, '      ')}
      user: { id: req.user.id, name: req.user.name },
      organizationId: req.user.organizationId, // your customer, for usage and limits per client
${tokenLines(values, '      ', 'req.user')}
    });
    res.set('Cache-Control', 'no-store').json({ client_secret: session.clientSecret });
  } catch {
    res.status(502).end(); // temporary: the assistant retries
  }
});`,
  };
}

function nextjs(values: SnippetValues): Snippet {
  return {
    id: 'nextjs',
    title: `Next.js route handler (app${values.sessionPath}/route.ts)`,
    language: 'ts',
    code: `import { createPlatoSession } from '@plato/node';
import { getCurrentUser } from '@/lib/auth'; // your own sign-in

export async function POST() {
  const user = await getCurrentUser();
  if (!user) return new Response(null, { status: 401 });
  // Reads PLATO_SECRET_KEY from the environment.
  const session = await createPlatoSession({${apiOriginLine(values, '    ')}
    user: { id: user.id, name: user.name },
    organizationId: user.organizationId, // your customer, for usage and limits per client
${tokenLines(values, '    ', 'user')}
  });
  return Response.json(
    { client_secret: session.clientSecret },
    { headers: { 'Cache-Control': 'no-store' } },
  );
}`,
  };
}

function python(values: SnippetValues): Snippet {
  const server = values.server ?? 'your-server';
  const prefix = values.server === null ? '# ' : '';
  return {
    id: 'python',
    title: 'Python (Flask)',
    language: 'python',
    code: `import os
import requests
from flask import jsonify
from flask_login import current_user, login_required  # or your own sign-in


@app.post("${values.sessionPath}")
@login_required
def plato_session():
    response = requests.post(
        "${values.sessionsUrl}",
        headers={"Authorization": f"Bearer {os.environ['PLATO_SECRET_KEY']}"},
        json={
            "user": {
                "id": str(current_user.id),
                "name": current_user.name,
                "organization_id": str(current_user.organization_id),
            },
            "mcp_servers": [
${values.server === null ? "                # When your data is connected: the server name and this user's tokens for it.\n" : ''}                ${prefix}{
                ${prefix}    "name": "${server}",
                ${prefix}    "authorization_token": mint_access_token_for(current_user),
                ${prefix}    "refresh_token": issue_refresh_token_for(current_user),
                ${prefix}},
            ],
        },
        timeout=10,
    )
    if not response.ok:
        return "", 502  # temporary: the assistant retries
    return jsonify(client_secret=response.json()["client_secret"]), 200, {"Cache-Control": "no-store"}`,
  };
}

function apiOriginLine(values: SnippetValues, indent: string): string {
  return values.apiOrigin ? `\n${indent}apiOrigin: '${values.apiOrigin}',` : '';
}

function tokenLines(values: SnippetValues, indent: string, user: string): string {
  const lines = [
    `server: '${values.server ?? 'your-server'}',`,
    `accessToken: await mintAccessTokenFor(${user}), // this user's short-lived token for your MCP server`,
    `refreshToken: await issueRefreshTokenFor(${user}), // so Plato renews it without the page`,
  ];
  if (values.server !== null) return lines.map((line) => indent + line).join('\n');
  return [
    `${indent}// When your data is connected: the server name and this user's tokens for it.`,
    ...lines.map((line) => `${indent}// ${line}`),
  ].join('\n');
}
